import { useMutation } from "@apollo/client";
import { PracticeArea } from "@design-system";
import { PracticeAreasCascader } from "@design-system";

import { Button, Form, InputNumber } from "antd";
import React, { useEffect, useState } from "react";
import { UPDATE_ORGANISATIONAL_UNIT } from "../../../graphql/mutation/update-organisational-unit";
import styles from "./practice-area-container.css";
import { ORGANISATIONAL_UNITS_QUERY } from "../../../graphql/query";
import { VPDLabel } from "~/context/taxonomy-context";

type Props = {
  practiceAreas: PracticeArea[];
  loading: boolean;
  onChange: (val: any) => any;
};

export const PracticeAreaSettingsPage: React.FC<Props> = ({ practiceAreas, loading }) => {
  const [selectedPa, setSelectedPa] = useState<PracticeArea | undefined>();
  const [gpmDangerValue, setGpmDangerValue] = useState<boolean>(false);

  const [update] = useMutation(UPDATE_ORGANISATIONAL_UNIT, {
    refetchQueries: [{ query: ORGANISATIONAL_UNITS_QUERY }],
  });

  const PA_LABEL = VPDLabel("PracticeArea");

  const onSelectedPa = (id: number) => {
    const pa = practiceAreas.find((x) => x.id === id);
    if (!pa) {
      return;
    }
    setSelectedPa(pa);
  };

  const [form] = Form.useForm<PracticeArea>();

  const targetPa = practiceAreas.find((x) => x.id === selectedPa?.id);

  const initialValues = {
    id: targetPa?.id,
    gpmDangerThreshold: targetPa?.gpmDangerThreshold,
    gpmWarningThreshold: targetPa?.gpmWarningThreshold,
  };

  useEffect(() => {
    form.setFieldsValue(initialValues);
  }, [form, selectedPa]);

  return (
    <div>
      <Form
        form={form}
        labelAlign="left"
        labelCol={{ span: 4 }}
        wrapperCol={{ span: 4, offset: 1 }}
        onFinish={async () => {
          const validated = await form.validateFields();
          if (validated.id) {
            update({
              variables: {
                id: validated.id,
                input: {
                  gpmDangerThreshold: {
                    set: validated.gpmDangerThreshold ? Number(validated.gpmDangerThreshold) : null,
                  },
                  gpmWarningThreshold: {
                    set: validated.gpmWarningThreshold
                      ? Number(validated.gpmWarningThreshold)
                      : null,
                  },
                },
              },
            });
          }
        }}
      >
        <Form.Item
          label={PA_LABEL}
          name="id"
          rules={[
            {
              required: true,
              message: `Please select a ${PA_LABEL}`,
            },
          ]}
        >
          <PracticeAreasCascader onChange={(val) => onSelectedPa(val)} loading={loading} />
        </Form.Item>
        <Form.Item shouldUpdate label="GPM Required" name="gpmDangerThreshold">
          <InputNumber<number>
            max={100}
            min={0}
            formatter={(value) => (value ? `${value}%` : "")}
            parser={(value: any) => value && value.replace("%", "")}
            onChange={(value) => {
              if (value || value === 0) {
                setGpmDangerValue(true);
              } else {
                setGpmDangerValue(false);
                form.setFieldsValue({ gpmWarningThreshold: null });
              }
            }}
          />
        </Form.Item>

        <Form.Item
          shouldUpdate
          label="GPM Warning Threshold"
          name="gpmWarningThreshold"
          rules={[
            {
              required: gpmDangerValue,
              message: "Please enter the Gross Profit Margin (GPM) Warning threshold",
            },
          ]}
        >
          <InputNumber<number>
            max={100}
            min={0}
            formatter={(value) => (value ? `${value}%` : "")}
            parser={(value: any) => value && value.replace("%", "")}
          />
        </Form.Item>

        <Form.Item wrapperCol={{ span: 1 }} shouldUpdate>
          <>
            <Button type="primary" htmlType="submit" className={styles["submit-button"]}>
              Save
            </Button>
          </>
        </Form.Item>
      </Form>
    </div>
  );
};
